import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BriefcaseBusiness,
  CircleUserRound,
  FilePlus2,
  FolderKanban,
  Inbox,
  ShieldCheck,
} from "lucide-react";
import CockpitShell from "../components/CockpitShell";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const navItems = [
  { href: "/dashboard/employer", label: "Uebersicht", icon: BriefcaseBusiness },
  { href: "/dashboard/account", label: "Konto", icon: CircleUserRound },
  { href: "/dashboard/stb/create", label: "Neue Anzeige", icon: FilePlus2 },
  { href: "/dashboard/stb/manage", label: "Verwalten", icon: FolderKanban },
  { href: "/dashboard/stb/applications", label: "Bewerbungen", icon: Inbox },
];

const funnelSignals = [
  { label: "Control Deck", value: "Aktiv", note: "Create, Manage und Bewerbungen bleiben der operative Kern." },
  { label: "Decision Stage", value: "Lean", note: "Decision Summary bleibt kompakt statt cockpithaft." },
  { label: "Rollout", value: "Scoped", note: "Kein Matching, kein Ranking, kein Voll-Cockpit." },
];

const funnelSteps = [
  { title: "Anzeige starten", note: "Neue Positionen strukturiert anlegen.", cta: "Neue Anzeige", href: "/dashboard/stb/create" },
  { title: "Anzeigen steuern", note: "Lifecycle, Status und Sichtbarkeit klar fuehren.", cta: "Verwalten", href: "/dashboard/stb/manage" },
  { title: "Bewerbungen entscheiden", note: "Anzeigengebundene Bewerbungen schnell lesen.", cta: "Bewerbungen", href: "/dashboard/stb/applications" },
];

const guardrails = [
  "Marketplace ist hier kein primaerer Arbeitsmodus.",
  "Matching, Ranking und Voll-Cockpit bleiben ausserhalb dieses Sprints.",
  "Die Decision Summary bleibt signalstark und bewusst kompakt.",
];

const EmployerDashboard = () => {
  const navigate = useNavigate();
  const [detailTab, setDetailTab] = useState("funnel");

  useEffect(() => {
    document.title = "Experts4Europe | Employer Dashboard";
  }, []);

  return (
    <CockpitShell
      title="Hiring Control Deck"
      subtitle="Ein breiter, entscheidungsorientierter Arbeitsraum mit einer dominanten Funnel-Buehne und einer kompakten Action Rail statt verteilter Verwaltungskacheln."
      badge="Experts4Europe Employer"
      navItems={navItems}
      wideLayout
      theme="employer"
      shellLabel="Hiring Control Deck"
      shellDescription="Anzeige, Verwaltung und Bewerbungen laufen in einer gemeinsamen light-first Entscheidungsstruktur mit klaren Guardrails."
      modeLabel="Decision Funnel"
      liveLabel="Employer"
      topSignal="Employer System"
      topContext="Hiring Control Deck"
      actions={
        <>
          <Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={() => navigate("/dashboard/stb/manage")}>
            Anzeigen verwalten
          </Button>
          <Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={() => navigate("/dashboard/stb/applications")}>
            Bewerbungen
          </Button>
          <Button className="dashboard-action-button" onClick={() => navigate("/dashboard/stb/create")}>
            Neue Anzeige
          </Button>
        </>
      }
    >
      <div className="dashboard-body dashboard-body-employer">
        <div className="dashboard-main-stage">
          <section className="dashboard-mission-board">
            <div className="dashboard-section-head">
              <div>
                <span className="dashboard-kicker">Decision Funnel Stage</span>
                <h2 className="dashboard-section-title">Was laeuft. Was stockt. Was heute entschieden wird.</h2>
                <p className="dashboard-section-copy">Eine Hauptbuehne fuer den Hiring-Kernpfad statt Broschueren-Text oder Kachelwiese.</p>
              </div>
            </div>

            <div className="dashboard-summary-grid dashboard-summary-grid-employer">
              {funnelSignals.map((item, index) => (
                <article key={item.label} className={`dashboard-summary-card${index === 0 ? " dashboard-summary-card-highlight" : ""}`}>
                  <span className="dashboard-summary-label">{item.label}</span>
                  <strong>{item.value}</strong>
                  <p>{item.note}</p>
                </article>
              ))}
            </div>

            <Tabs value={detailTab} onValueChange={setDetailTab} className="dashboard-local-tabs">
              <TabsList className="dashboard-tablist">
                <TabsTrigger value="funnel" className="dashboard-tabtrigger">Funnel</TabsTrigger>
                <TabsTrigger value="actions" className="dashboard-tabtrigger">Kernaktionen</TabsTrigger>
                <TabsTrigger value="scope" className="dashboard-tabtrigger">Scope</TabsTrigger>
              </TabsList>

              <TabsContent value="funnel" className="dashboard-tabcontent">
                <div className="dashboard-step-list">
                  {funnelSteps.map((step) => (
                    <article key={step.title} className="dashboard-step-card">
                      <div className="dashboard-step-icon"><BriefcaseBusiness size={18} strokeWidth={2} /></div>
                      <div className="dashboard-step-copy">
                        <div className="dashboard-step-head">
                          <h3>{step.title}</h3>
                          <span className="dashboard-pill dashboard-pill-soft">Core</span>
                        </div>
                        <p>{step.note}</p>
                      </div>
                      <Button variant="outline" className="dashboard-inline-button" onClick={() => navigate(step.href)}>
                        {step.cta}
                      </Button>
                    </article>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="actions" className="dashboard-tabcontent">
                <div className="dashboard-surface-grid dashboard-surface-grid-slim">
                  <div className="dashboard-surface">
                    <span className="dashboard-summary-label">Dominanter CTA</span>
                    <strong>Neue Anzeige</strong>
                    <p>Der breiteste Einstieg bleibt die neue Anzeige, nicht ein Dekorahmen.</p>
                  </div>
                  <div className="dashboard-surface">
                    <span className="dashboard-summary-label">Breiter Arbeitsraum</span>
                    <strong>Keine Mittelsaeule</strong>
                    <p>Die Hauptflaeche nutzt Breite fuer Funnel und Entscheidungen statt fuer Leerraum.</p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="scope" className="dashboard-tabcontent">
                <div className="dashboard-surface-grid">
                  {guardrails.map((item) => (
                    <div key={item} className="dashboard-surface">
                      <span className="dashboard-summary-label">Guardrail</span>
                      <p>{item}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </section>
        </div>

        <aside className="dashboard-secondary-rail">
          <section className="dashboard-rail-card">
            <div className="dashboard-section-head dashboard-section-head-compact">
              <div>
                <span className="dashboard-kicker">Action Rail</span>
                <h2 className="dashboard-section-title">Die drei Bewegungen</h2>
              </div>
            </div>
            <div className="dashboard-action-stack">
              {funnelSteps.map((step, index) => (
                <button
                  key={step.title}
                  type="button"
                  className={`dashboard-stack-button${index === 0 ? " is-primary" : ""}`}
                  onClick={() => navigate(step.href)}
                >
                  <span>{step.cta}</span>
                  <ArrowGlyph />
                </button>
              ))}
            </div>
          </section>

          <section className="dashboard-rail-card">
            <div className="dashboard-section-head dashboard-section-head-compact">
              <div>
                <span className="dashboard-kicker">Rollout Rules</span>
                <h2 className="dashboard-section-title">Kompakt lesbar</h2>
              </div>
            </div>
            <div className="dashboard-note">
              <ShieldCheck size={16} strokeWidth={2} />
              <p>Keine Premium-Huelle fuer Verwaltungslogik. Der Arbeitgeberbereich bleibt Entscheidungssystem.</p>
            </div>
            <div className="dashboard-rail-metrics">
              <article className="dashboard-metric-card">
                <span className="dashboard-summary-label">Freigegeben</span>
                <strong>Create / Manage / Bewerbungen</strong>
                <p>Alles andere bleibt bewusst nachgeordnet.</p>
              </article>
              <article className="dashboard-metric-card">
                <span className="dashboard-summary-label">Nicht Teil</span>
                <strong>Matching / Ranking</strong>
                <p>Kein Cockpit-Vollausbau in diesem Sprint.</p>
              </article>
            </div>
          </section>
        </aside>
      </div>
    </CockpitShell>
  );
};

function ArrowGlyph() {
  return <span aria-hidden="true">→</span>;
}

export default EmployerDashboard;
